/**
 * @fileoverview Hook for rendering and animating a circular progress indicator on a canvas.
 * The progress indicator shows an arc that completes over the specified duration.
 *
 * @example
 * function ProgressIndicator() {
 *   const canvasRef = useRef<HTMLCanvasElement>(null);
 *
 *   useProgress({
 *     canvas: canvasRef.current,
 *     duration: 5000, // 5 seconds
 *     color: '#4338ca',
 *     onEnd: () => console.log('Progress complete')
 *   });
 *
 *   return <canvas ref={canvasRef} width={100} height={100} />;
 * }
 */


/**
 * @interface ProgressProps
 * @description The properties for the progress indicator
 * @property {HTMLCanvasElement} canvas - The canvas element to draw the progress indicator on
 * @property {string} color - The color of the progress indicator
 * @property {number} duration - The duration of the progress indicator in milliseconds
 * @property {() => void} onEnd - The function to call when the progress indicator is complete
 */
interface ProgressProps {
    canvas: HTMLCanvasElement
    color?: string
    duration: number
    onEnd?: () => void
}

/**
 * @function useProgress
 * @description Hook for rendering and animating a circular progress indicator on a canvas
 * @param {ProgressProps} props - The properties for the progress indicator
 * @returns {Object} An object with a start method to begin the animation
 */
export function useProgress({ canvas, color = '#4338ca', duration, onEnd }: ProgressProps) {
    if (!canvas) {
        return
    }

    const context = canvas.getContext('2d')
    const stroke = 2 * devicePixelRatio
    const radius = (canvas.width - 2 * stroke) / 2
    const circ = Math.PI * 2
    const quart = Math.PI / 2
    const x0 = canvas.width / 2
    const y0 = canvas.height / 2

    let progress = 0
    let lastTimestamp = 0

    context.strokeStyle = color
    context.lineWidth = stroke
    context.fillStyle = '#fff'

    function draw() {
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.beginPath()
        context.arc(x0, y0, radius, -quart, (circ * progress) / duration - quart)
        context.stroke()
    }

    function animate() {
        const timestamp = performance.now()

        if (lastTimestamp) {
            progress += timestamp - lastTimestamp
        }

        draw()

        lastTimestamp = timestamp
        if (progress <= duration) {
            requestAnimationFrame(() => {
                animate()
            })
        } else {
            onEnd?.()
        }
    }

    return {
        start() {
            animate()
        },
    }
}
